import { defineAsyncComponent } from "vue";
import { Checkbox, Form, Input, Skeleton, Tooltip } from "ant-design-vue";
import lodash from "lodash";

const TemplateBuilder = defineAsyncComponent({ loader: () => import("./TemplateBuilder"), loadingComponent: Skeleton });
const Image = defineAsyncComponent({ loader: () => import("./image/Image"), loadingComponent: Skeleton });
const CSelect = defineAsyncComponent({ loader: () => import("./select/CSelect"), loadingComponent: Skeleton });
const CDatePicker = defineAsyncComponent({ loader: () => import("./date-picker/CDatePicker"), loadingComponent: Skeleton });
const CDateRangePicker = defineAsyncComponent({ loader: () => import("./date-picker/CDateRangePicker"), loadingComponent: Skeleton });
const CTreeSelect = defineAsyncComponent({ loader: () => import("./tree-select/CTreeSelect"), loadingComponent: Skeleton });
const InputNumber = defineAsyncComponent({ loader: () => import("./input-number/InputNumber"), loadingComponent: Skeleton });
const CRadioGroup = defineAsyncComponent({ loader: () => import("./radio-group/CRadioGroup"), loadingComponent: Skeleton });
const Attachment = defineAsyncComponent({ loader: () => import("./attachment/Attachment"), loadingComponent: Skeleton });
const CardBorder = defineAsyncComponent({ loader: () => import("../../card/CardBorder"), loadingComponent: Skeleton });

export const getLabel = ({ field }) => {
    if (!field.label && !field.labelRender) {
        return null;
    }
    if (typeof field.labelRender === "string") {
        return field.labelRender;
    } else if (field.labelRender) {
        return field.labelRender({ field });
    }
    if (typeof field.label === "string") {
        return field.label;
    } else if (field.label) {
        return field.label({ field });
    }
    return null;
};

export const customUseForm = ({ props, field }) => {
    const result = {};
    const modelRef = props.useForm?.modelRef;
    const rulesRef = props.useForm?.rulesRef;
    const rules = rulesRef?.[field.key];
    const value = lodash.get(modelRef, field.key);
    if (rules) {
        const required = rules.some(rule => rule.when?.({ rule, value }));
        if (required) {
            result.required = true;
        }
    }
    return result;
};

export const createFieldImage = options => {
    return lodash.merge(
        {
            visible: () => true,
            skeletonProp: () => ({ paragraph: true }),
            inputRender: ({ field }) => {
                const inputProp = options?.inputProp?.({ field });
                return <Image {...inputProp}></Image>;
            },
            changeLog: {
                getValue: ({ record }) => lodash.get(record, `${options?.key}.name`)
            }
        },
        options
    );
};

export const createFieldInput = options => {
    return lodash.merge(
        {
            visible: () => true,
            inputRender: ({ field }) => {
                const placeholder = options?.label?.();
                const inputProp = options?.inputProp?.({ field });
                if (inputProp?.["v-maska"]) {
                    return <Input placeholder={placeholder} {...inputProp} v-maska={inputProp["v-maska"]}></Input>;
                } else {
                    return <Input placeholder={placeholder} {...inputProp}></Input>;
                }
            }
        },
        options
    );
};

export const createFieldInputPassword = options => {
    return lodash.merge(
        {
            visible: () => true,
            inputRender: ({ field }) => {
                const placeholder = options?.label?.();
                const inputProp = options?.inputProp?.({ field });
                return <Input.Password placeholder={placeholder} {...inputProp}></Input.Password>;
            }
        },
        options
    );
};

export const createFieldTextArea = options => {
    return lodash.merge(
        {
            visible: () => true,
            inputRender: ({ field }) => {
                const placeholder = options?.label?.();
                const inputProp = options?.inputProp?.({ field });
                return <Input.TextArea class="mt-1" placeholder={placeholder} autoSize={true} {...inputProp}></Input.TextArea>;
            }
        },
        options
    );
};

export const createFieldSelect = options => {
    return lodash.merge(
        {
            visible: () => true,
            inputRender: ({ field }) => {
                const placeholder = options?.label?.();
                const inputProp = options?.inputProp?.({ field });
                return <CSelect placeholder={placeholder} {...inputProp}></CSelect>;
            },
            changeLog: {
                getValue: ({ field, record }) => {
                    const inputProp = options?.inputProp?.({ field });
                    if (inputProp.mode === "multiple") return lodash.get(record, options?.key);
                    return lodash.get(record, `${options?.key}.name`);
                }
            }
        },
        options
    );
};

export const createFieldDate = options => {
    return lodash.merge(
        {
            visible: () => true,
            inputRender: ({ field }) => {
                const inputProp = options?.inputProp?.({ field });
                return <CDatePicker {...inputProp}></CDatePicker>;
            }
        },
        options
    );
};

export const createFieldDateRange = options => {
    return lodash.merge(
        {
            visible: () => true,
            inputRender: ({ field }) => {
                const inputProp = options?.inputProp?.({ field });
                return <CDateRangePicker {...inputProp}></CDateRangePicker>;
            }
        },
        options
    );
};

export const createFieldDatetime = options => {
    return lodash.merge(
        {
            visible: () => true,
            inputRender: ({ field }) => {
                const inputProp = options?.inputProp?.({ field });
                return <CDatePicker format="DD/MM/YYYY HH:mm" valueFormat="YYYY-MM-DD[T]HH:mm:ssZ" showTime={{ format: "HH:mm" }} {...inputProp}></CDatePicker>;
            }
        },
        options
    );
};

export const createFieldDatetimeRange = options => {
    return lodash.merge(
        {
            visible: () => true,
            inputRender: ({ field }) => {
                const inputProp = options?.inputProp?.({ field });
                return <CDateRangePicker format="DD/MM/YYYY HH:mm" showTime={{ format: "HH:mm" }} {...inputProp}></CDateRangePicker>;
            }
        },
        options
    );
};

export const createFieldInputNumber = options => {
    return lodash.merge(
        {
            visible: () => true,
            inputRender: ({ field }) => {
                const placeholder = options?.label?.();
                const inputProp = options?.inputProp?.({ field });
                return <InputNumber placeholder={placeholder} {...inputProp}></InputNumber>;
            }
        },
        options
    );
};

export const createFieldTreeSelect = options => {
    return lodash.merge(
        {
            visible: () => true,
            inputRender: ({ field }) => {
                const placeholder = options?.label?.();
                const inputProp = options?.inputProp?.({ field });
                return <CTreeSelect placeholder={placeholder} {...inputProp}></CTreeSelect>;
            },
            changeLog: {
                getValue: ({ record }) => lodash.get(record, `${options?.key}.name`)
            }
        },
        options
    );
};

export const createFieldRadioGroup = options => {
    return lodash.merge(
        {
            visible: () => true,
            inputRender: ({ field }) => {
                const inputProp = options?.inputProp?.({ field });
                return <CRadioGroup {...inputProp}></CRadioGroup>;
            },
            changeLog: {
                getValue: ({ record }) => lodash.get(record, `${options?.key}.name`)
            }
        },
        options
    );
};

export const createFieldCheckbox = options => {
    return lodash.merge(
        {
            visible: () => true,
            inputRender: ({ field }) => {
                const inputProp = options?.inputProp?.({ field });
                const text = inputProp?.text?.();
                return (
                    <Checkbox {...inputProp}>
                        <span>{text}</span>
                    </Checkbox>
                );
            }
        },
        options
    );
};

export const createFieldToolTipCheckbox = options => {
    return lodash.merge(
        {
            visible: () => true,
            inputRender: ({ field }) => {
                const inputProp = options?.inputProp?.({ field });
                const text = inputProp?.text?.();
                const tooltip = inputProp?.tooltip?.();
                const placement = inputProp?.placement;
                return (
                    <Tooltip title={tooltip} placement={placement}>
                        <Checkbox {...inputProp}>
                            <span>{text}</span>
                        </Checkbox>
                    </Tooltip>
                );
            }
        },
        options
    );
};

export const createFieldTemplateBuilder = options => {
    return lodash.merge(
        {
            visible: () => true,
            formItemRender: ({ field }) => {
                const formItemProp = options?.formItemProp?.({ field });
                const label = options?.label?.();
                const inputProp = options?.inputProp?.({ field });
                const cardBorderProp = options?.cardBorderProp?.({ field });
                if ((formItemProp || label) && cardBorderProp) {
                    return (
                        <CardBorder {...cardBorderProp}>
                            <Form.Item label={label} {...formItemProp}>
                                <TemplateBuilder {...inputProp}></TemplateBuilder>
                            </Form.Item>
                        </CardBorder>
                    );
                }
                if (formItemProp || label) {
                    return (
                        <Form.Item label={label} {...formItemProp}>
                            <TemplateBuilder {...inputProp}></TemplateBuilder>
                        </Form.Item>
                    );
                }
                if (cardBorderProp) {
                    return (
                        <CardBorder {...cardBorderProp}>
                            <TemplateBuilder {...inputProp}></TemplateBuilder>
                        </CardBorder>
                    );
                }
                return <TemplateBuilder {...inputProp}></TemplateBuilder>;
            }
        },
        options
    );
};

export const createFieldAttachment = options => {
    return lodash.merge(
        {
            visible: () => true,
            inputRender: ({ field }) => {
                const inputProp = options?.inputProp?.({ field });
                return <Attachment {...inputProp}></Attachment>;
            },
            changeLog: {
                getValue: ({ record }) => lodash.get(record, `${options?.key}.name`)
            }
        },
        options
    );
};
